import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useThree } from '@react-three/fiber';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onAddEntityIdsToSelection, onRemoveSelection, onSetBackgroundHotSpotEnabled, onSetMarkerIndexPressed } from '../../../store/actions';
import { HOTSPOT_SCALE, IShapeTypes, maths, useGetScreenComponents } from '../../../utils';
import Plane from '../Plane/Plane';
import { isAbstractComponent, removeNonEntityIntersections } from '../r3f-components/utils/general';
import { getWorldPositionForScreenEntity } from './hotspotUtils';
const BackgroundHotspot = ({ namePrefix, parentLocalSpace = false }) => {
    // Redux
    const dispatch = useDispatch();
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    const backgroundHotspotIsEnabled = useSelector((state) => state.userReducer.backgroundHotspotIsEnabled);
    const rotationIsActive = useSelector((state) => state.userReducer.rotationIsActive);
    const rotationMarkerPressed = useSelector((state) => state.userReducer.markerIndexPressed === 999);
    const contentEntityDict = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const activeScene = contentEntityDict[activeSceneId];
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const selectableScreenRelativeComponentIds = useGetScreenComponents(activeSceneId, true);
    // State
    const [initialPosition, setInitialPosition] = useState(null);
    const [dragAreaPosition, setDragAreaPosition] = useState(null);
    const [dragAreaScale, setDragAreaScale] = useState([0, 0, 0]);
    const [oldPosition, setOldPosition] = useState(null);
    const { scene } = useThree();
    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    useEffect(() => {
        return () => {
            dispatch(onSetBackgroundHotSpotEnabled(false));
        };
    }, []);
    const pointerDownHandler = (e) => {
        // Return early if an entity is in the intersection array ( takes priority )
        if (removeNonEntityIntersections(e.intersections).length > 0)
            return;
        if (e.buttons === 2)
            return;
        e.target.setPointerCapture(e.pointerId);
        const { x, y, z } = parentLocalSpace ? e.eventObject.parent.worldToLocal(e.point.clone()) : e.point;
        if (!rotationMarkerPressed) {
            dispatch(onRemoveSelection(selectedEntityIds));
        }
        if (!rotationMarkerPressed)
            dispatch(onSetBackgroundHotSpotEnabled(true));
        if (isMountedRef.current)
            setInitialPosition([x, y, z]);
    };
    const pointerMoveHandler = (e) => {
        if (e.buttons === 2)
            return;
        const { x, y, z } = parentLocalSpace ? e.eventObject.parent.worldToLocal(e.point.clone()) : e.point;
        // console.log(x, y, z)
        if (!initialPosition || (initialPosition[0] === x && initialPosition[1] === y && initialPosition[2] === z))
            return;
        // Return if position has not changed (re-render and/or continuous firing)
        if (oldPosition && maths.vec3.equal([x, y, z], oldPosition))
            return;
        if (isMountedRef.current)
            setOldPosition([x, y, z]);
        // Calc drag area (scale, coords & position)
        const newDragAreaScale = [(x - initialPosition[0]) / 2, (y - initialPosition[1]) / 2, 0];
        const dragAreaCoords = [initialPosition, [x, initialPosition[1], 0], [x, y, z], [initialPosition[0], y, 0]];
        // Check for drag area changes (using 'old' drag component state)
        let xIncreased = false;
        let yIncreased = false;
        if (Math.abs(dragAreaScale[0]) - Math.abs(newDragAreaScale[0]) <= 0)
            xIncreased = true;
        if (Math.abs(dragAreaScale[1]) - Math.abs(newDragAreaScale[1]) <= 0)
            yIncreased = true;
        // Only then update state
        if (isMountedRef.current) {
            setDragAreaPosition([initialPosition[0] + (x - initialPosition[0]) / 2, initialPosition[1] + (y - initialPosition[1]) / 2, -0.001]);
            setDragAreaScale(newDragAreaScale);
        }
        // If drag x & y increase filter out selected entitis (no need to check if selected)
        let entityIdsToCheck = !isScreenRelativeMode ? activeScene.children : selectableScreenRelativeComponentIds;
        if (xIncreased && yIncreased) {
            entityIdsToCheck = entityIdsToCheck.filter((id) => !selectedEntityIds.includes(id) || !contentEntityDict[id].isLocked);
            // if drag x & y decrease only check selected entities (no need to check non-selected ones)
        }
        else if (!xIncreased && !yIncreased) {
            entityIdsToCheck = selectedEntityIds;
        }
        const addIdArray = [];
        const removeIdArray = [];
        for (let i = 0; i < entityIdsToCheck.length; i++) {
            const id = entityIdsToCheck[i];
            const component = contentEntityDict[id];
            if (isAbstractComponent(component))
                continue;
            const { position, rotation, scale } = component;
            const isLocked = contentEntityDict[id].isLocked;
            if (isLocked)
                continue;
            const worldPosition = getWorldPositionForScreenEntity(id, activeSceneId, componentsById, position, scene) ?? position;
            const entityHotspotCoords = maths.localToWorldPosition2d(rotation[2], [worldPosition[0], worldPosition[1]], [
                [-scale[0], scale[1]],
                [scale[0], scale[1]],
                [scale[0], -scale[1]],
                [-scale[0], -scale[1]],
            ]);
            const entityIsCompletely = maths.isCompletelyInOrOutOfDragArea(entityHotspotCoords, dragAreaCoords);
            if (entityIsCompletely.inside) {
                if (!selectedEntityIds.includes(id))
                    addIdArray.push(id);
                return;
            }
            if (entityIsCompletely.outside) {
                if (selectedEntityIds.includes(id))
                    removeIdArray.push(id);
                return;
            }
            if (maths.areasAreColliding2d(entityHotspotCoords, dragAreaCoords, rotation[2], IShapeTypes.rect)) {
                if (!selectedEntityIds.includes(id))
                    addIdArray.push(id);
                continue;
            }
            if (selectedEntityIds.includes(id))
                removeIdArray.push(id);
        }
        if (addIdArray.length > 0) {
            dispatch(onAddEntityIdsToSelection(addIdArray));
        }
        if (removeIdArray.length > 0 && selectedEntityIds.length > 0) {
            dispatch(onRemoveSelection(removeIdArray));
        }
    };
    const pointerUpHandler = (e) => {
        e.target.releasePointerCapture(e.pointerId);
        if (e.buttons === 2)
            return;
        dispatch(onSetBackgroundHotSpotEnabled(false));
        dispatch(onSetMarkerIndexPressed(null));
        if (isMountedRef.current) {
            setDragAreaPosition(null);
            setDragAreaScale([0, 0, 0]);
            setInitialPosition(null);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Plane, { enabled: false, name: `${namePrefix}Background Hotspot 1`, visible: backgroundHotspotIsEnabled, scale: [Math.abs(dragAreaScale[0]), Math.abs(dragAreaScale[1]), Math.abs(dragAreaScale[0])], position: dragAreaPosition ?? [0, 0, 0], rotation: [0, 0, 0], color: [74, 143, 227, 0.13], renderOrder: 0 }), _jsx(Plane, { visible: false, name: `${namePrefix}Background Hotspot 2`, enabled: !rotationIsActive && !rotationMarkerPressed, position: [0, 0, -0.001], rotation: [0, 0, 0], scale: HOTSPOT_SCALE, pointerUpHandler: pointerUpHandler, pointerMoveHandler: pointerMoveHandler, pointerDownHandler: pointerDownHandler })] }));
};
export default React.memo(BackgroundHotspot);
